import { Component, effect, inject, OnInit, signal, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { FolderFormsPageStore } from './folder-forms-page.store';
import { Validators } from '@angular/forms';
import { SubscriptionComponent } from '../../../../shared/helpers/subscription.component';
import {
    FolderFormDataObjectDepre,
    FolderFormObjectDepre,
    FormObject,
} from '@it2go/types';
import { isInvalid } from '../../../../shared/helpers/validate';
import { ConfirmBuilder } from '../../../../shared/service/confirm.builder';
import { FormSetEnum } from '../../../../../../../../types/src/services/enum/form-set.enum';
import { TableColumn } from '../../../../shared/components/kit/table/table.types';
import {
    fb,
    isAllowed,
    routeParams,
} from '../../../../shared/shared.global-service';
import { FolderFormsRecordModalComponent } from '../../../components/services/forms/folder-forms-record-modal/folder-forms-record-modal.component';
import { FolderFormsPageStore2 } from './folder-forms-page.store2';
import { contentGlobalSuccess } from '../../../content.error';
import { AppFormGroup } from '../../../../../utils/form.utils';
import { SwitchInputComponent } from '../../../../../components/kit/input/switch-input/switch-input.component';
import { ButtonComponent } from '../../../../../components/kit/button/button.component';
import { TableComponent } from '../../../../shared/components/kit/table/table.component';
import { AsyncPipe } from '@angular/common';
import { PrimeTemplate } from '@openng/optimus-ui/api';
import { Tooltip } from '@openng/optimus-ui/tooltip';
import { TranslatePipe } from '@ngx-translate/core';
import { FormEsasTableComponent } from '../../../components/services/forms/form-esas-table/form-esas-table.component';
import { FormMsseTableComponent } from '../../../components/services/forms/form-msse-table/form-msse-table.component';
import { FormIadlTableComponent } from '../../../components/services/forms/form-iadl-table/form-iadl-table.component';
import { FormDeknTableComponent } from '../../../components/services/forms/form-dekn-table/form-dekn-table.component';
import { FormPpsTableComponent } from '../../../components/services/forms/form-pps-table/form-pps-table.component';
import { FormOpzTableComponent } from '../../../components/services/forms/form-opz-table/form-opz-table.component';
import { FormBiTableComponent } from '../../../components/services/forms/form-bi-table/form-bi-table.component';
import { FormNiTableComponent } from '../../../components/services/forms/form-ni-table/form-ni-table.component';
import { FormRvdTableComponent } from '../../../components/services/forms/form-rvd-table/form-rvd-table.component';
import { Dialog } from '@openng/optimus-ui/dialog';
import { TextInputComponent } from '../../../../../components/kit/input/text-input/text-input.component';
import { SelectInputComponent } from '../../../../../components/kit/input/select-input/select-input.component';
import { ActionButtonsComponent } from '../../../../../components/kit/action-buttons/action-buttons.component';
import { CheckInputComponent } from '../../../../../components/kit/input/check-input/check-input.component';
import { FolderGlobalStore } from '../../../../../store/medicalService/folder.global-store';
import { BackgroundTaskLaunchService } from '../../../../layout/store/background-tasks/background-task-launch.service';
import {
    BackgroundActionType,
    ClientDocumentExportKind,
} from '@libs/types/src/notifications/types/background-action.types';

@Component({
    selector: 'app-folder-forms-page',
    templateUrl: './folder-forms-page.component.html',
    styleUrls: ['./folder-forms-page.component.scss'],
    providers: [FolderFormsPageStore, FolderFormsPageStore2],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        SwitchInputComponent,
        ButtonComponent,
        TableComponent,
        AsyncPipe,
        PrimeTemplate,
        Tooltip,
        TranslatePipe,
        FormEsasTableComponent,
        FormMsseTableComponent,
        FormIadlTableComponent,
        FormDeknTableComponent,
        FormPpsTableComponent,
        FormOpzTableComponent,
        FormBiTableComponent,
        FormNiTableComponent,
        FormRvdTableComponent,
        Dialog,
        TextInputComponent,
        SelectInputComponent,
        ActionButtonsComponent,
        CheckInputComponent,
        FolderFormsRecordModalComponent,
    ],
})
export class FolderFormsPageComponent extends SubscriptionComponent implements OnInit {

    public readonly store = inject(FolderFormsPageStore);
    public readonly store2 = inject(FolderFormsPageStore2);
    private readonly backgroundTaskLauncher = inject(
        BackgroundTaskLaunchService,
    );
    public readonly confirmBuilder = inject(ConfirmBuilder);
    protected readonly folderStore = inject(FolderGlobalStore);

    @ViewChild(FolderFormsRecordModalComponent)
    folderFormRecord!: FolderFormsRecordModalComponent;

    folderId: number | null = null;

    form = new AppFormGroup({
        showDeactivated: fb.control<boolean>(false),
    });
    formOptions: FormObject[] = [];

    folderFormForm = fb.group({
        id: fb.control<number | null>(null),
        name: fb.control<string | null>(null, Validators.required),
        form: fb.control<number | null>(null, Validators.required),
        comment: fb.control<string | null>(null),
        active: fb.control<boolean>(true),
    });

    printFormDataForm = fb.group({
        id: fb.control<number | null>(null),
        archive: fb.control<boolean>(true),
    });

    printFormDataModalVisible = false;
    folderFormFormModalVisible = false;

    readonly columns: TableColumn[] = [
        { name: 'name', dataKey: 'name' },
        { name: 'form', dataKey: 'uniqueIdentifier' },
        { name: 'comment', dataKey: 'comment' },
        {
            name: 'numberOfRecords',
            dataKey: null,
            data: (item: any) => item.folderFormData.length,
        },
    ];

    protected printing = false;
    protected readonly resource = 'form';
    protected showDeactivatedChange = signal<boolean>(false);

    constructor() {
        super();
        this.store.doOnActions(
            () => this.loadForms(),
            'createFolderForm',
            'updateFolderForm',
            'deleteFolderForm',
            'createFolderFormData',
            'updateFolderFormData',
            'deleteFolderFormData',
        );

        this.store.doOnActions(
            () => contentGlobalSuccess.next('common.updated'),
            'updateFolderForm',
            'updateFolderFormData',
        );

        this.store.doOnActions(
            () => contentGlobalSuccess.next('common.created'),
            'createFolderForm',
            'createFolderFormData',
        );

        effect(() => this.loadForms());
    }

    public ngOnInit(): void {
        this.subs.push(
            this.form.controls.showDeactivated.valueChanges.subscribe(
                (showDeactivated) => {
                    this.showDeactivatedChange.set(showDeactivated ?? false);
                },
            ),
            this.store.data.formsV2$.subscribe((forms) => {
                if (forms) {
                    this.formOptions = forms.items;
                }
            }),
            this.folderFormForm.controls.form.valueChanges.subscribe(
                (formId) => {
                    if (formId && !this.folderFormForm.controls.name.value) {
                        const formName =
                            this.formOptions.find((form) => form.id === formId)
                                ?.name || null;
                        this.folderFormForm.controls.name.setValue(formName);
                    }
                },
            ),
        );
        if (isAllowed['form.update']()) {
            this.columns.push({ name: 'changeActivation' });
        } else {
            this.folderFormForm.disable();
        }
    }

    protected async submitFolderForm(): Promise<void> {
        if (isInvalid(this.folderFormForm)) return;

        const data = {
            name: this.folderFormForm.value.name || '',
            comment: this.folderFormForm.value.comment || null,
        };

        if (this.folderFormForm.value.id) {
            this.store.api.updateFolderForm({
                id: this.folderFormForm.value.id,
                active: this.folderFormForm.value.active,
                ...data,
            } as any);
        } else {
            this.store.api.createFolderForm({
                folderId: Number(routeParams.folderId()),
                formId: this.folderFormForm.value.form,
                ...data,
            } as any);
        }

        this.closeFolderForm();
    }

    openEditFolderForm(folderForm: FolderFormObjectDepre) {
        this.folderFormForm.patchValue({
            id: folderForm.id,
            name: folderForm.name,
            form: folderForm.formId,
            comment: folderForm.comment,
            active: folderForm.active,
        });
        this.folderFormForm.get('form')?.disable();
        this.folderFormFormModalVisible = true;
    }

    closeFolderForm() {
        this.folderFormForm.reset();
        this.folderFormForm.get('form')?.enable();
        this.folderFormFormModalVisible = false;
    }

    changeActiveStatusOfFolderForm(folderForm: FolderFormObjectDepre) {
        this.store.api.updateFolderForm({
            id: folderForm.id,
            name: folderForm.name,
            comment: folderForm.comment,
            active: !folderForm.active,
        });
    }

    deleteFolderForm(event: any) {
        this.confirmBuilder.confirm(
            'folder.forms.deleteFolderFormConfirmation.header',
            'folder.forms.deleteFolderFormConfirmation.label',
            () => {
                this.store.api.deleteFolderForm({ id: event.item.id });
            },
            { name: 'folderFormDeleteConfirm' + event.item.id },
            'pi-trash',
            event.event.target,
        );
    }

    deleteRecordFormData(event: any) {
        this.confirmBuilder.confirm(
            'folder.forms.deleteFolderFormDataConfirmation.header',
            'folder.forms.deleteFolderFormDataConfirmation.label',
            () => {
                this.store.api.deleteFolderFormData({ id: event.item.id });
            },
            { name: 'folderFormDataDeleteConfirm' + event.item.id },
            'pi-trash',
            event?.event?.target,
        );
    }

    openPrintFormDataModal(folderForm: FolderFormDataObjectDepre) {
        this.printing = false;
        this.printFormDataForm.patchValue({
            id: folderForm.id,
            archive: true,
        });
        this.printFormDataModalVisible = true;
    }

    closePrintFormDataModal() {
        this.printFormDataModalVisible = false;
        this.printFormDataForm.reset();
    }

    async printRecordFormData(): Promise<void> {
        if (this.printing) return;
        this.printing = true;
        const id = this.printFormDataForm.controls.id.value;
        if (!id) {
            this.printing = false;
            return;
        }
        try {
            const started = await this.backgroundTaskLauncher.start({
                type: BackgroundActionType.CLIENT_DOCUMENT_EXPORT,
                payload: {
                    folderId: Number(routeParams.folderId()),
                    kind: ClientDocumentExportKind.FOLDER_FORM,
                    input: {
                        id,
                        archive:
                            this.printFormDataForm.controls.archive.value ??
                            false,
                    },
                },
                alreadyRunningMessage:
                    'person.documentExport.backgroundTask.messages.alreadyRunning',
                failedMessage:
                    'person.documentExport.backgroundTask.messages.startFailed',
            });
            if (started) this.closePrintFormDataModal();
        } finally {
            this.printing = false;
        }
    }

    protected loadForms() {
        const folderId = routeParams.folderId();
        const formsSharingIds = this.store.formsSharingIds() || [];
        const showDeactivated = this.showDeactivatedChange();
        if (folderId) {
            // TODO refactorovat na grid az bude cas
            this.store.api.getFolderForms({
                folderIds: [Number(folderId), ...formsSharingIds],
                showDeactivated,
            });
        }
    }

    protected readonly FormSetEnum = FormSetEnum;
    protected readonly isAllowed = isAllowed;
}
